// Fit and Engagement (GRO-381) for every table that draws them: the strategist board, Available,
// the leadership Accounts view and the AE table. One read, cached under one key, so moving between
// the pages costs no second fetch.
//
// Off until the server says `scores_visible` for this viewer's resolved role (`SCOUT_SCORES_VISIBLE`:
// everyone on a Render preview, only an admin on production while scoring runs in shadow mode, GRO-460). A failed read, or an empty table before the first
// scoring run, leaves the columns out rather than breaking the page.
//
// The flag is read from `/api/me` until the book has answered (GRO-491). `/api/me` answers in
// milliseconds and the book in seconds, so the scores are fetched alongside the book rather than
// after it, and `waiting` lets a board hold its table until they arrive: one render, in its final
// order, instead of rows that re-sort when the score columns land.

import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchMe, fetchScores } from '../api/client';
import type { TokenGetter } from '../api/client';
import type { ScoresPayload, Viewer } from '../api/types';
import { anyScores, holdForScores, SCORES_WAIT_MILLISECONDS } from './scores';

export interface BoardScores {
  payload: ScoresPayload | undefined;
  // Whether the score columns are drawn: visible, and at least one account scored.
  shown: boolean;
  // Whether the board should keep showing its loading line: scores are visible and not here yet.
  waiting: boolean;
}

export function useScores(getToken: TokenGetter, bookViewer: Viewer | null | undefined): BoardScores {
  // The same `['me']` read the landing redirect and the tab bar use, so it is usually cached.
  const meQuery = useQuery({ queryKey: ['me'], queryFn: () => fetchMe(getToken) });
  const viewer = bookViewer ?? meQuery.data?.viewer;
  const scoresVisible = Boolean(viewer?.scores_visible);

  const scoresQuery = useQuery({
    queryKey: ['scores'],
    queryFn: () => fetchScores(getToken),
    enabled: scoresVisible,
    retry: false,
  });

  // Stop holding the table after SCORES_WAIT_MILLISECONDS; the columns then land whenever the read does.
  const scoresPending = scoresVisible && scoresQuery.isPending;
  const [gaveUp, setGaveUp] = useState(false);
  useEffect(() => {
    if (!scoresPending) return undefined;
    const timer = setTimeout(() => {
      console.warn(`scores did not load in time; drawing the board without them. waitMilliseconds=${SCORES_WAIT_MILLISECONDS}`);
      setGaveUp(true);
    }, SCORES_WAIT_MILLISECONDS);
    return () => clearTimeout(timer);
  }, [scoresPending]);

  // A failed read draws the board without scores; say so in the console for whoever is debugging.
  const scoresError = scoresQuery.error;
  useEffect(() => {
    if (scoresError) console.warn(`scores could not be read; drawing the board without them. error=${String(scoresError)}`);
  }, [scoresError]);

  const payload = scoresVisible ? scoresQuery.data : undefined;
  return {
    payload,
    shown: anyScores(payload),
    waiting: holdForScores(scoresVisible, scoresPending, gaveUp),
  };
}
